import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { NgModule, Injector, DoBootstrap, ApplicationRef, inject, Type, createNgModule, NgModuleRef, provideCheckNoChangesConfig } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { createCustomElement } from '@angular/elements';
import { EsiFooterComponent } from './esi-factory/warapper/esi-footer.component';

import { EsiHeaderComponent } from './esi-factory/warapper/esi-header.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';

interface LazyCustomElementDefinition {
    selector: string;
    loadComponent: () => Promise<Type<unknown>>;
    loadInjector?: (parentInjector: Injector) => Promise<Injector>;
}

const lazyCustomElements: LazyCustomElementDefinition[] = [
    {
        selector: 'video-multi-sources-player',
        loadComponent: () =>
            import('./video-player/video-multi-sources-player/video-multi-sources-player.component').then((m) => m.VideoMultiSourcesPlayerComponent),
        loadInjector: async (parentInjector: Injector) => {
            const { VideoPlayerModule } = await import('./video-player/video-player.module');
            return createNgModule(VideoPlayerModule, parentInjector).injector;
        },
    },
    {
        selector: 'sfirat-omer',
        loadComponent: () => import('./omer/sfirat-haomer.component').then((m) => m.SfiratHaOmerComponent),
    },
    {
        selector: 'user-serieses',
        loadComponent: () => import('./user/user-serieses/user-serieses.component').then((m) => m.UserSeriesesComponent),
    },
    {
        selector: 'pdf-viewer-external',
        loadComponent: () => import('./content/content-detail/pdf-viewer-external/pdf-viewer-external.component').then((m) => m.PdfViewerExternalComponent),
    },
    {
        selector: 'zoom-player',
        loadComponent: () => import('./video-player/zoom-player/zoom-player.component').then((m) => m.ZoomPlayerComponent),
    },
];

@NgModule({
    imports: [
        BrowserModule,
        AppModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: false, // environment.production, //ngServ cannot be used with service worker
            // Register the ServiceWorker as soon as the app is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000',
            // registrationStrategy: 'registerImmediately',
            scope: '/',
        }),
    ],
    providers: [
        // { provide: APP_BASE_HREF, useValue: '/' },
        ...(environment.production ? [] : [provideCheckNoChangesConfig({ exhaustive: true, interval: 10000 })]),
    ],
    // bootstrap: [AppComponent]
})
export class AppBrowserModule implements DoBootstrap {
    appRef = inject(ApplicationRef);
    private injector = inject(Injector);
    private lazyModuleRefs: NgModuleRef<unknown>[] = [];

    ngDoBootstrap(app: ApplicationRef) {
        void this._bootstrapApplication(app);
    }

    private async _bootstrapApplication(app: ApplicationRef): Promise<void> {
        this._registerCustomElement('app-esi-header', EsiHeaderComponent, this.injector);

        // Bootstrap the main app only if the selector is present on the page.
        if (document.querySelector('angular-app')) {
            app.bootstrap(AppComponent);
        }

        this._registerCustomElement('app-esi-footer', EsiFooterComponent, this.injector);

        this._scheduleLazyCustomElementsRegistration();
    }

    private _scheduleLazyCustomElementsRegistration(): void {
        const register = async () => {
            await this._registerLazyCustomElements();
        };

        const registerWhenIdle = () => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => void register(), { timeout: 3000 });
                return;
            }

            setTimeout(() => void register(), 1500);
        };

        if (document.readyState === 'complete') {
            registerWhenIdle();
            return;
        }

        // Delay non-critical custom-element registration until after load and initial idle time.
        window.addEventListener('load', registerWhenIdle, { once: true });
    }

    private async _registerLazyCustomElements(): Promise<void> {
        for (const customElement of lazyCustomElements) {
            const component = await customElement.loadComponent();
            const elementInjector = customElement.loadInjector ? await this._createLazyElementInjector(customElement) : this.injector;
            this._registerCustomElement(customElement.selector, component, elementInjector);
        }
    }

    private async _createLazyElementInjector(customElement: LazyCustomElementDefinition): Promise<Injector> {
        const { loadInjector } = customElement;
        if (!loadInjector) {
            return this.injector;
        }

        const injector = await loadInjector(this.injector);
        const moduleRef = injector.get(NgModuleRef, null);
        if (moduleRef) {
            this.lazyModuleRefs.push(moduleRef);
        }
        return injector;
    }

    private _registerCustomElement(selector: string, component: Type<unknown>, injector: Injector): void {
        if (customElements.get(selector)) {
            return;
        }

        const element = createCustomElement(component, {
            injector,
        });
        customElements.define(selector, element);
    }
}
